import Btn from 'components/buttons/Btn'
import { isArray, kebabCase } from 'lodash-es'
import PropTypes from 'prop-types'
import { Col, Modal, Row } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { useSimpleState } from 'utils'
import ActBtn from './buttons/ActBtn'

/**
 * @param show {boolean}
 * @param id {?string} Use if you want to do some extra styling - otherwise, it's based on the title (which would cause
 * trouble with CSS since it might get translated)
 * @param size {'sm'|'md'|'lg'|'xl'} Changes the modal size
 * @param beforeClose {function} Runs before doClose() executes, if given. Usually for state cleanup.
 * @param doClose {function} Usually comes from outside, controlling the modal state.
 * @param doSubmit {function} If it returns boolean false, it won't close the popup; otherwise, it closes.
 * @param disabled {boolean|string} If on, it won't be possible to submit the form. If it's a string, will show as a tooltip over the disabled submit button
 * @param dangerous {boolean} If true, the confirm button gets red instead of the default green
 * @param backdrop {boolean|'static'} If a backdrop should be included; if 'static', the user won`t be able to close the modal by clicking outside (see [docs](https://react-bootstrap-v4.netlify.app/components/modal/#static-backdrop)).
 * @param title="Are you sure?" {?string}
 * @param confirmTexts {string[]} Two/three states, for ready/saving/saved. Defaults to ["Ok","Ok"].
 * @param cancelText="Cancel" {?string}
 * @param cancelGoBack=false {boolean} Uses "No, go back" on the cancel button, to be extra clear when confirming removals/cancellations. Gets overridden by `cancelText` - don't use both together.
 * @param confirmIcon {string|string[]}
 * @param freezeBody {boolean} Causes the body to only change after the modal is shown again; useful when outside state changes shouldn't reflect immediately on the modal, such as when they toggle something and then fade out.
 * @param body
 * @return {JSX.Element}
 */
export default function ConfirmModal({
	show,
	id,
	size = 'sm',
	beforeClose = null,
	doClose,
	doSubmit,
	disabled = false,
	dangerous = false,
	backdrop = true,
	title = null,
	confirmTexts = null,
	cancelText = null,
	cancelGoBack = false,
	confirmIcon = null,
	freezeBody = false,
	children,
}) {
	const { t }   = useTranslation()
	id           ||= 'modal-'+kebabCase(title)
	cancelText   ||= cancelGoBack? t('No, go back') : t('Cancel', { context: 'go back' })
	title        ||= t('Are you sure?')
	confirmTexts ||= [t('Ok'), t('Ok')]

	if (id == 'modal-') {
		console.error('ConfirmModal requires either a title or an id')
	}

	//simply setting the body to be in the modal's internal state instead of using the markup directly allows us to hold the body change until the modal finishes hiding (I guess the state changes get queued?). Using `children` directly caused the modal to change its body as soon as the outside state changed, making an "on/off" confirmation to flip its contents while the modal was hiding - but also allowing the modal to have an interactive content, such as the NewAdjustment modal. Using the state, the modal is able to hide completely before its markup changes.
	const frozenBody = useSimpleState(children)
	function getBody() {
		return freezeBody? frozenBody.val : children
	}

	function onShow() {
		if (freezeBody) {
			frozenBody.set(children) //updates it only when re-showing the modal, in case the outside markup changed
		}
	}

	function runClose() {
		if (beforeClose) {
			beforeClose()
		}
		doClose()
	}

	async function doAction() {
		if (await doSubmit() !== false) {
			runClose()
		}
	}

	let body
	return <Modal {...{size, show, id, backdrop, onShow }} onHide={runClose}>
		<Modal.Header closeButton>
			<Modal.Title>{title}</Modal.Title>
		</Modal.Header>

		{((body = getBody()) && typeof body == 'object' && !isArray(body) && body.type.displayName == 'ListGroup')?
			body : <Modal.Body>{body}</Modal.Body>}

		<Modal.Footer as={Row}>
			<Col xs="auto">
				<Btn onClick={runClose} size="sm" variant="outline">{cancelText}</Btn>
			</Col>
			<Col>
				{/* TODO this gets way too large when the modal is md/lg/xl on larger screens; limit it, but still try to make it larger than the cancel button... maybe make its column xs=auto as well, but with larger margins on wider screens? */}
				<ActBtn block variant={dangerous? 'danger' : 'success'} disabled={disabled} icon={confirmIcon} texts={confirmTexts} action={doAction}/>
			</Col>
		</Modal.Footer>
	</Modal>
}

ConfirmModal.propTypes = {
	show        : PropTypes.bool.isRequired,
	id          : PropTypes.string,
	size        : PropTypes.oneOf(['md', 'sm', 'lg', 'xl']),
	beforeClose : PropTypes.func,
	doClose     : PropTypes.func.isRequired,
	doSubmit    : PropTypes.func.isRequired,
	disabled    : PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	dangerous   : PropTypes.bool,
	backdrop    : PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
	title       : PropTypes.string,
	confirmTexts: PropTypes.arrayOf(PropTypes.string),
	cancelText  : PropTypes.string,
	confirmIcon : PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
	freezeBody  : PropTypes.bool,
	children    : PropTypes.node.isRequired,
}
